import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import GenericCategoryDropdown from './GenericCategoryDropdown';
import { CATEGORY_LEVELS } from './CategoryTable';
import { useGetMainCategoriesQuery, useGetSecondCategoriesQuery, useGetSubCategoriesQuery } from '../../../../api/endpoints/faultcodes';
import { useLanguage } from '../../../../contexts/LanguageContext';

const CategoryTableFilter = ({ currentCategoryLevel, filters, onApplyFilters, onResetFilters }) => {
    const { th } = useLanguage();
    const [filterOpen, setFilterOpen] = useState(false);
    const [localFilters, setLocalFilters] = useState({
        mainCategory: filters.mainCategory || null,
        secondaryCategory: filters.secondaryCategory || null,
        subCategory: filters.subCategory || null,
        subPartCategory: filters.subPartCategory || null
    });
    const [localSearchText, setLocalSearchText] = useState('');

    const [mainResetCount, setMainResetCount] = useState(0);
    const [secondResetCount, setSecondResetCount] = useState(0);
    const [subResetCount, setSubResetCount] = useState(0);
    const [subPartResetCount, setSubPartResetCount] = useState(0);

    useEffect(() => {
        setLocalFilters({
            mainCategory: filters.mainCategory || null,
            secondaryCategory: filters.secondaryCategory || null,
            subCategory: filters.subCategory || null,
            subPartCategory: filters.subPartCategory || null
        });
    }, [filters]);

    const showMainDropdown = useMemo(() => currentCategoryLevel === CATEGORY_LEVELS.SECONDARY || currentCategoryLevel === CATEGORY_LEVELS.SUB || currentCategoryLevel === CATEGORY_LEVELS.SUB_PART, [currentCategoryLevel]);
    const showSecondDropdown = useMemo(() => currentCategoryLevel === CATEGORY_LEVELS.SUB || currentCategoryLevel === CATEGORY_LEVELS.SUB_PART, [currentCategoryLevel]);
    const showSubDropdown = useMemo(() => currentCategoryLevel === CATEGORY_LEVELS.SUB_PART, [currentCategoryLevel]);

    const dataSelectorForCategories = useCallback((apiResponse) => {
        return apiResponse?.data || [];
    }, []);

    const handleMainCategoryChange = useCallback((selectedObject) => {
        setLocalFilters((prev) => ({
            ...prev,
            mainCategory: selectedObject,
            secondaryCategory: null,
            subCategory: null,
            subPartCategory: null
        }));
        setSecondResetCount((c) => c + 1);
        setSubResetCount((c) => c + 1);
        setSubPartResetCount((c) => c + 1);
    }, []);

    const handleSecondaryCategoryChange = useCallback((selectedObject) => {
        setLocalFilters((prev) => ({
            ...prev,
            secondaryCategory: selectedObject,
            subCategory: null,
            subPartCategory: null
        }));
        setSubResetCount((c) => c + 1);
        setSubPartResetCount((c) => c + 1);
    }, []);

    const handleSubCategoryChange = useCallback((selectedObject) => {
        setLocalFilters((prev) => ({
            ...prev,
            subCategory: selectedObject,
            subPartCategory: null
        }));
        setSubPartResetCount((c) => c + 1);
    }, []);

    const handleSearchTextChange = useCallback((e) => {
        const newSearchText = e.target.value;
        setLocalSearchText(newSearchText);
    }, []);

    const handleFilterToggle = () => {
        setFilterOpen((prev) => !prev);
    };

    const renderCustomFilters = useCallback(() => {
        const itemIdFieldForCategories = 'lookupCode';
        const PAGE_LOAD_ROW_COUNT = 100;

        return (
            <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                    <div className="field mb-2 p-1">
                        <label htmlFor="searchTextFilter" className="block mb-1 text-sm">
                            {th('common.search')}
                        </label>
                        <InputText id="searchTextFilter" value={localSearchText} onChange={handleSearchTextChange} placeholder={th('faultCodes.enterSearchText')} className="w-full" />
                    </div>
                </div>

                {showMainDropdown && (
                    <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                        <div className="field mb-2 p-1">
                            <label htmlFor="mainCategoryFilter" className="block mb-1 text-sm">
                                {th('faultCodes.mainCategory')}
                            </label>
                            <GenericCategoryDropdown
                                value={localFilters.mainCategory}
                                onChange={handleMainCategoryChange}
                                useQueryHook={useGetMainCategoriesQuery}
                                baseQueryArgs={{}}
                                dataSelector={dataSelectorForCategories}
                                itemLabelField="mainCategoryName"
                                itemIdField={itemIdFieldForCategories}
                                placeholder={th('faultCodes.common.selectMainCategory')}
                                disabled={false}
                                resetTrigger={mainResetCount}
                                skipQuery={false}
                                rowCount={PAGE_LOAD_ROW_COUNT}
                                className="w-full"
                            />
                        </div>
                    </div>
                )}

                {showSecondDropdown && (
                    <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                        <div className="field mb-2 p-1">
                            <label htmlFor="secondaryCategoryFilter" className="block mb-1 text-sm">
                                {th('faultCodes.secondCategory')}
                            </label>
                            <GenericCategoryDropdown
                                value={localFilters.secondaryCategory}
                                onChange={handleSecondaryCategoryChange}
                                useQueryHook={useGetSecondCategoriesQuery}
                                baseQueryArgs={{
                                    mainCategory: typeof localFilters.mainCategory === 'object' ? localFilters.mainCategory?.[itemIdFieldForCategories] : localFilters.mainCategory
                                }}
                                dataSelector={dataSelectorForCategories}
                                itemLabelField="secondaryCategoryName"
                                itemIdField={itemIdFieldForCategories}
                                placeholder={th('faultCodes.common.selectSecondCategory')}
                                disabled={!localFilters.mainCategory}
                                resetTrigger={secondResetCount}
                                skipQuery={!localFilters.mainCategory}
                                rowCount={PAGE_LOAD_ROW_COUNT}
                                className="w-full"
                            />
                        </div>
                    </div>
                )}

                {showSubDropdown && (
                    <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                        <div className="field mb-2 p-1">
                            <label htmlFor="subCategoryFilter" className="block mb-1 text-sm">
                                {th('faultCodes.subCategory')}
                            </label>
                            <GenericCategoryDropdown
                                value={localFilters.subCategory}
                                onChange={handleSubCategoryChange}
                                useQueryHook={useGetSubCategoriesQuery}
                                baseQueryArgs={{
                                    mainCategory: typeof localFilters.mainCategory === 'object' ? localFilters.mainCategory?.[itemIdFieldForCategories] : localFilters.mainCategory,
                                    secondaryCategory: typeof localFilters.secondaryCategory === 'object' ? localFilters.secondaryCategory?.[itemIdFieldForCategories] : localFilters.secondaryCategory
                                }}
                                dataSelector={dataSelectorForCategories}
                                itemLabelField="subCategoryName"
                                itemIdField={itemIdFieldForCategories}
                                placeholder={th('faultCodes.common.selectSubCategory')}
                                disabled={!localFilters.mainCategory || !localFilters.secondaryCategory}
                                resetTrigger={subResetCount}
                                skipQuery={!localFilters.mainCategory || !localFilters.secondaryCategory}
                                rowCount={PAGE_LOAD_ROW_COUNT}
                                className="w-full"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }, [
        localFilters,
        localSearchText,
        showMainDropdown,
        showSecondDropdown,
        showSubDropdown,
        mainResetCount,
        secondResetCount,
        subResetCount,
        handleMainCategoryChange,
        handleSecondaryCategoryChange,
        handleSubCategoryChange,
        handleSearchTextChange,
        dataSelectorForCategories,
        th
    ]);

    const handleApplyFilters = useCallback(() => {
        const processedFilters = {
            mainCategory: typeof localFilters.mainCategory === 'object' ? localFilters.mainCategory?.lookupCode : localFilters.mainCategory || null,
            secondaryCategory: typeof localFilters.secondaryCategory === 'object' ? localFilters.secondaryCategory?.lookupCode : localFilters.secondaryCategory || null,
            subCategory: typeof localFilters.subCategory === 'object' ? localFilters.subCategory?.lookupCode : localFilters.subCategory || null,
            subPartCategory: typeof localFilters.subPartCategory === 'object' ? localFilters.subPartCategory?.lookupCode : localFilters.subPartCategory || null
        };

        onApplyFilters(processedFilters, localSearchText);
    }, [localFilters, localSearchText, onApplyFilters]);

    const handleResetFilters = useCallback(() => {
        setLocalFilters({
            mainCategory: null,
            secondaryCategory: null,
            subCategory: null,
            subPartCategory: null
        });
        setLocalSearchText('');

        setMainResetCount((c) => c + 1);
        setSecondResetCount((c) => c + 1);
        setSubResetCount((c) => c + 1);
        setSubPartResetCount((c) => c + 1);

        onResetFilters();
    }, [onResetFilters]);

    return (
        <div className="generic-table-filter mb-2">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('filter.advancedFilter')} onClick={handleFilterToggle} outlined={!filterOpen} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>
                    {renderCustomFilters()}
                    <div className="flex justify-content-start mt-3">
                        <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleResetFilters} />
                        <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApplyFilters} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryTableFilter;